import { useEffect, useState } from 'react';

import { useDomainsStore } from '../../../hooks';

import {
  Modal,
  Box,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  FormHelperText,
  FormControl,
} from '@mui/material';

const apiTypes = ['facebook', 'google'];
const serverNames = {
  facebook: ['namecheap', 'cloudflare'],
  google: ['netcup', 'inwx'],
};
const selectedFields = {
  namecheap: {
    api: ['apiKey', 'apiUsername', 'apiIp'],
  },
  cloudflare: {
    api: ['apiKey', 'binomeIp'],
  },
  netcup: {
    api: ['apiKey', 'apiPassword', 'customerNumber'],
  },
  inwx: {
    api: ['username', 'password', 'dedicatedIP', 'nameServers'],
  },
};

export const AddApiKeysModal = ({ openModal, setOpenModal }) => {
  const { userList, createApiKeys } = useDomainsStore();

  const [selectedBuyer, setSelectedBuyer] = useState('');
  const [apiType, setApiType] = useState('');
  const [serverName, setServerName] = useState('');
  const [apiFields, setApiFields] = useState({});

  const [errors, setErrors] = useState({
    selectedBuyer: false,
    apiType: false,
    serverName: false,
    apiFields: false,
  });

  useEffect(() => {
    if (serverName) {
      const fields = selectedFields[serverName];
      if (fields && typeof fields === 'object' && !Array.isArray(fields)) {
        const initial = {};
        Object.keys(fields).forEach((group) => {
          fields[group].forEach((field) => {
            initial[field] = '';
          });
        });
        setApiFields(initial);
      } else if (Array.isArray(fields)) {
        setApiFields(fields.reduce((acc, field) => ({ ...acc, [field]: '' }), {}));
      }
    }
  }, [serverName]);

  const handleCloseModal = () => {
    setSelectedBuyer('');
    setApiType('');
    setServerName('');
    setApiFields({});
    setErrors({ selectedBuyer: false, apiType: false, serverName: false, apiFields: false });
    setOpenModal(false);
  };

  const handleSubmit = () => {
    const newErrors = {
      selectedBuyer: !selectedBuyer,
      apiType: !apiType,
      serverName: !serverName,
      apiFields: Object.values(apiFields).some((value) => !value),
    };

    setErrors(newErrors);

    if (Object.values(newErrors).some((error) => error)) {
      return;
    }

    createApiKeys({ buyer: selectedBuyer, apiType, serverName, ...apiFields });

    handleCloseModal();
  };

  const handleFieldChange = (field, value) => {
    setApiFields((prev) => ({ ...prev, [field]: value }));
  };

  const btnDisabled =
    Object.values(errors).some((err) => err) ||
    !Object.values(apiFields).every((value) => value.length > 0) ||
    !selectedBuyer ||
    !apiType ||
    !serverName;

  return (
    <Modal
      open={openModal}
      onClose={handleCloseModal}
      aria-labelledby='add-api-keys-modal'
      aria-describedby='modal-to-add-api-keys-to-buyer'
    >
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          backgroundColor: 'black',
          padding: 3,
          borderRadius: 2,
          boxShadow: 24,
          width: 400,
          maxHeight: 'calc(100vh - 200px)',
          overflow: 'auto',
        }}
      >
        <Typography variant='h6' component='h2' gutterBottom>
          Добавить API ключи
        </Typography>

        <FormControl fullWidth error={errors.selectedBuyer} sx={{ marginBottom: 2 }}>
          <Select displayEmpty value={selectedBuyer} onChange={(e) => setSelectedBuyer(e.target.value)}>
            <MenuItem value='' disabled>
              Выберите баера
            </MenuItem>
            {userList.map((user) => (
              <MenuItem key={user._id} value={user.login}>
                {user.login}
              </MenuItem>
            ))}
          </Select>
          {errors.selectedBuyer && <FormHelperText>Выберите баера</FormHelperText>}
        </FormControl>

        <FormControl fullWidth error={errors.apiType} sx={{ marginBottom: 2 }}>
          <Select
            displayEmpty
            value={apiType}
            onChange={(e) => {
              setApiType(e.target.value);
              setServerName('');
              setApiFields({});
            }}
          >
            <MenuItem value='' disabled>
              Выберите API
            </MenuItem>
            {apiTypes.map((name) => (
              <MenuItem key={name} value={name}>
                {name}
              </MenuItem>
            ))}
          </Select>
          {errors.apiType && <FormHelperText>Выберите API</FormHelperText>}
        </FormControl>

        <FormControl fullWidth error={errors.serverName} sx={{ marginBottom: 2 }}>
          <Select displayEmpty value={serverName} onChange={(e) => setServerName(e.target.value)} disabled={!apiType}>
            <MenuItem value='' disabled>
              {apiType ? 'Выберите имя сервера' : 'Выберите API'}
            </MenuItem>
            {(serverNames[apiType] || []).map((name) => (
              <MenuItem key={name} value={name}>
                {name}
              </MenuItem>
            ))}
          </Select>
          {errors.serverName && <FormHelperText>Выберите имя сервера</FormHelperText>}
        </FormControl>

        {serverName && (
          <>
            {typeof selectedFields[serverName] === 'object' && !Array.isArray(selectedFields[serverName])
              ? Object.keys(selectedFields[serverName]).map((group) => (
                  <Box key={group} sx={{ marginBottom: 2 }}>
                    <Typography variant='subtitle2' sx={{ color: 'white', marginBottom: 1 }}>
                      {group === 'registrant' ? 'Данные регистранта' : 'API данные'}
                    </Typography>
                    {selectedFields[serverName][group].map((field) => {
                      const keyName = `${group}.${field}`;
                      return (
                        <TextField
                          key={keyName}
                          label={field}
                          variant='outlined'
                          fullWidth
                          value={apiFields[field] || ''}
                          onChange={(e) => handleFieldChange(field, e.target.value)}
                          sx={{ marginBottom: 1 }}
                          error={!!errors.apiFields && !(apiFields[field] && apiFields[field].length > 0)}
                          helperText={
                            errors.apiFields && !(apiFields[field] && apiFields[field].length > 0)
                              ? 'Это поле необходимо'
                              : ''
                          }
                        />
                      );
                    })}
                  </Box>
                ))
              : selectedFields[serverName].map((field) => (
                  <TextField
                    key={field}
                    label={field}
                    variant='outlined'
                    fullWidth
                    value={apiFields[field] || ''}
                    onChange={(e) => handleFieldChange(field, e.target.value)}
                    sx={{ marginBottom: 2 }}
                    error={errors.apiFields && !(apiFields[field] && apiFields[field].length > 0)}
                    helperText={
                      errors.apiFields && !(apiFields[field] && apiFields[field].length > 0)
                        ? 'Это поле необходимо'
                        : ''
                    }
                  />
                ))}
          </>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', marginTop: 2 }}>
          <Button onClick={handleCloseModal} variant='outlined' color='secondary'>
            Отмена
          </Button>
          <Button onClick={handleSubmit} variant='contained' color='primary' disabled={btnDisabled}>
            Добавить
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};
